import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { Field } from 'formik';

import * as Styled from './ConfirmationCodeInput.styled';

/**
 * A confirmation code input allows a user to enter a 6 digit numeric confirmation code. Intended to be rendered with a Formik Form
 */
const ConfirmationCodeInput = ({
  id,
  name,
  validate,
  className,
  ...inputProps
}) => (
  <div className={className}>
    <Field name={name} validate={validate}>
      {({ field, meta }) => {
        const hasError = meta.touched && meta.error;

        const displayCode = useMemo(() => {
          const codeArr = meta.value.split('');
          const digits = codeArr.length;
          const displayArr = [...codeArr, ...Array(6 - digits)];

          return displayArr.map((num, index) => (
            <Styled.DisplayNum
              isActive={index === digits}
              key={index}
              data-testid="ConfirmationCodeInput__num"
            >
              {num}
            </Styled.DisplayNum>
          ));
        }, [meta.value]);

        return (
          <>
            <Styled.ConfirmationCodeInput hasError={hasError}>
              <Styled.Input
                id={id}
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength="6"
                autoComplete="one-time-code"
                {...field}
                {...inputProps}
              />
              <Styled.DisplayContainer hasError={hasError} htmlFor={id}>
                <Styled.FieldDescription>
                  Confirmation Code{' '}
                </Styled.FieldDescription>
                {displayCode}
              </Styled.DisplayContainer>
            </Styled.ConfirmationCodeInput>
            {hasError && <Styled.Error>{meta.error}</Styled.Error>}
          </>
        );
      }}
    </Field>
  </div>
);

ConfirmationCodeInput.propTypes = {
  /**
   * CSS class name
   */
  className: PropTypes.string,
  /**
   * Form ID use to associate the label and input
   * Use specific naming, as a collision can cause undesirable effects.
   */
  id: PropTypes.string.isRequired,
  /**
   * Formik field name
   */
  name: PropTypes.string.isRequired,
  /**
   * Formik validation function
   */
  validate: PropTypes.func
};

ConfirmationCodeInput.defaultProps = {
  className: null,
  validate: undefined
};

export default ConfirmationCodeInput;
